import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2 } from "lucide-react";
import type { GmailMessagePreview } from "@/hooks/use-gmail";
import { extractEmail, extractName, formatEmailDate } from "./email-format";

interface MessageRowProps {
    message: GmailMessagePreview;
    isSelected: boolean;
    onToggle: () => void;
    disabled: boolean;
}

/** A single selectable message within a thread in the Gmail import modal. */
export function MessageRow({ message, isSelected, onToggle, disabled }: MessageRowProps) {
    return (
        <div
            className={`flex items-start gap-2.5 p-2 rounded text-xs transition-colors w-0 min-w-full overflow-hidden ${
                message.alreadyImported
                    ? "bg-muted/50 opacity-50"
                    : isSelected
                      ? "bg-primary/10"
                      : "hover:bg-muted/50"
            }`}
        >
            <Checkbox
                checked={isSelected}
                onCheckedChange={onToggle}
                disabled={disabled}
                className="mt-0.5 shrink-0"
            />
            <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex items-center justify-between gap-2 mb-0.5 min-w-0 overflow-hidden">
                    <span className="font-medium line-clamp-1 break-all block min-w-0">
                        {extractName(message.from)}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {message.alreadyImported && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0">
                                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                                Imported
                            </Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                            {formatEmailDate(message.date)}
                        </span>
                    </div>
                </div>
                <p className="text-muted-foreground line-clamp-1 break-all">{message.snippet}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1 break-all">
                    From: {extractEmail(message.from)} → To: {message.to.split(",").map((t) => extractEmail(t.trim())).join(", ")}
                </p>
            </div>
        </div>
    );
}
